<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div class="d-flex align-center px-4 pt-3 flex-wrap">
        <span class="text-lg font-weight-bold red--text text--darken-2 mr-auto pr-3">
          新增特休後須至特休假審核，通過才會生效
        </span>
        <v-btn
          class="ms-auto"
          small
          color="primary"
          outlined
          @click="toggleDisplayUnit"
        >
          單位: {{ displayUnit === "hours" ? "小時" : "天" }}
          <v-icon small class="ml-1">mdi-swap-horizontal</v-icon>
        </v-btn>
      </div>

      <v-skeleton-loader
        v-if="loading"
        type="table"
        class="mx-4"
      ></v-skeleton-loader>

      <template v-else>
        <info-dialog
          :visible.sync="infoDialog.visible"
          :title="infoDialog.title"
          :message="infoDialog.message"
          :highlight-message="infoDialog.highlightMessage"
          yes-button-text="確定"
          :yes-callback="infoDialog.yesCallback"
        />
        <div
          class="d-flex align-center justify-space-between flex-wrap mx-4 mb-3"
        >
          <div class="text-body-2 grey--text text--darken-1"></div>
          <v-btn
            class="
              font-weight-bold
              text-capitalize
              bg-gradient-success
              px-6
              py-3
              mt-2
            "
            color="success"
            :disabled="!selectedEmployeeIds.length || bulkGrantLoading"
            :loading="bulkGrantLoading"
            @click="grantAnnualLeave(true)"
          >
            新增特休
          </v-btn>
        </div>

        <v-data-table
          class="py-3 annual-leave-table"
          :headers="headers"
          :items="employees"
          item-key="id"
          hide-default-footer
          :show-select="true"
          v-model="selectedEmployees"
          :show-expand="false"
          single-expand
          :expanded.sync="expanded"
          disable-pagination
          :items-per-page="itemsPerPage"
          mobile-breakpoint="0"
        >
          <template v-slot:item.expectedSignDate="{ item }">
            <formatted-date
              v-if="item.expectedSignDate"
              :date="item.expectedSignDate"
              format-string="yyyy-MM-dd"
            />
            <span v-else>-</span>
          </template>
          <template v-slot:item.annualAvailable="{ item }">
            {{ convertMinutesToDays(item.annualLeaveAvailable) }}
          </template>
          <template v-slot:item.annualMax="{ item }">
            {{ convertMinutesToDays(item.annualLeaveMax) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center align-center">
              <v-btn small text color="primary" @click="openEditDialog(item)">
                <v-icon small left>mdi-pencil</v-icon>
                編輯可用天數
              </v-btn>
            </div>
          </template>
        </v-data-table>

        <div class="text-center my-4">
          <v-pagination
            v-model="currentPage"
            :length="pageCount"
            @input="fetchLeaveBalances"
            prev-icon="fa fa-angle-left"
            next-icon="fa fa-angle-right"
            color="#cb0c9f"
            circle
          ></v-pagination>
        </div>
      </template>
      <v-dialog v-model="editDialog.visible" max-width="500px">
        <v-card>
          <v-card-title class="headline">調整特休可用天數</v-card-title>
          <v-card-text class="pt-4">
            <div v-if="editDialog.loading" class="text-center py-4">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>
            <div
              v-else-if="!editDialog.record"
              class="text-center py-4 text-body-1 red--text"
            >
              找不到當天有效的特休紀錄。
            </div>
            <v-form v-else ref="editForm">
              <div class="mb-4 text-body-1">
                <strong>員工：</strong>
                {{ editDialog.item ? editDialog.item.name : "" }}
                <br />
                <div class="text-caption grey--text mt-1">
                  有效區間：{{
                    editDialog.record.start_time
                      ? editDialog.record.start_time.substring(0, 10)
                      : ""
                  }}
                  ~
                  {{
                    editDialog.record.end_time
                      ? editDialog.record.end_time.substring(0, 10)
                      : ""
                  }}
                </div>
              </div>
              <v-text-field
                v-model.number="editDialog.usedDays"
                :label="`已使用 (${unitText})`"
                type="number"
                :step="displayUnit === 'hours' ? '1' : '0.5'"
                min="0"
                outlined
                dense
              ></v-text-field>
              <v-text-field
                :value="
                  (
                    Number(editDialog.totalDays || 0) -
                    Number(editDialog.usedDays || 0)
                  ).toFixed(1)
                "
                :label="`未使用 (${unitText})`"
                type="text"
                outlined
                dense
                disabled
              ></v-text-field>
              <div class="text-caption grey--text text-left">
                總額度定為:
                {{ editDialog.totalDays }}
                {{ unitText }}
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="editDialog.visible = false">取消</v-btn>
            <v-btn
              color="primary"
              class="btn-primary bg-gradient-primary px-4"
              :disabled="!editDialog.record"
              :loading="editDialog.saving"
              @click="saveEdit"
            >
              儲存
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="previewDialog.visible" max-width="75%" scrollable>
        <v-card>
          <v-card-title class="headline">新增特休預覽</v-card-title>
          <v-card-text style="height: 600px">
            <div class="mb-4 text-body-1">
              {{ previewDialog.message }}
            </div>
            <v-data-table
              :headers="previewHeaders"
              :items="previewRecords"
              hide-default-footer
              disable-pagination
              class="elevation-1"
              :item-class="rowClass"
            >
              <template v-slot:item.join_type="{ item }">
                <v-chip
                  x-small
                  :color="item.join_type === 2 ? 'orange' : 'blue'"
                  text-color="white"
                >
                  {{ item.join_type === 2 ? "部份工時" : "全工時" }}
                </v-chip>
              </template>
              <template v-slot:item.expected_sign_date="{ item }">
                <formatted-date
                  v-if="item.expected_sign_date"
                  :date="item.expected_sign_date"
                  format-string="yyyy-MM-dd"
                />
                <span v-else>-</span>
              </template>
              <template v-slot:item.years="{ item }">
                {{ item.min_years }} - {{ item.max_years }} 年
              </template>
              <template v-slot:item.days="{ item }">
                {{ formatPreviewDays(item) }}
              </template>
              <template v-slot:item.effective_date="{ item }">
                <formatted-date
                  v-if="item.start_time"
                  :date="item.start_time"
                  format-string="yyyy-MM-dd"
                />
                <span v-else>-</span>
              </template>
              <template v-slot:item.duration="{ item }">
                <div class="d-flex flex-row">
                  <formatted-date
                    v-if="item.start_time"
                    :date="item.start_time"
                  />
                  <span class="text-caption text-center">&ensp;-&ensp;</span>
                  <formatted-date v-if="item.end_time" :date="item.end_time" />
                </div>
              </template>
              <template v-slot:item.total_minutes="{ item }">
                {{ formatTotalDays(item) }}
              </template>
              <template v-slot:item.note="{ item }">
                <template v-if="item.join_type === 2 && item.formula_detail">
                  <v-tooltip bottom max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                        class="text-caption"
                        style="cursor: help"
                      >
                        {{ formatPartTimeNote(item) }}
                      </span>
                    </template>
                    <div class="text-caption">
                      <div><strong>部份工時特休計算公式</strong></div>
                      <div class="mt-1">
                        實際工時:
                        {{ item.formula_detail.actual_work_hours }} 小時
                      </div>
                      <div>
                        分母: 40×{{ item.base_weeks }}週 -
                        {{ item.national_holidays }}天×8 =
                        {{ item.formula_detail.denominator }} 小時
                      </div>
                      <div>
                        法定特休: {{ item.formula_detail.statutory_days }}天 × 8
                        = {{ item.formula_detail.statutory_hours }} 小時
                      </div>
                      <div class="mt-1">
                        <strong
                          >特休時數 =
                          {{ item.formula_detail.leave_hours }} 小時</strong
                        >
                      </div>
                    </div>
                  </v-tooltip>
                </template>
                <template v-else>
                  {{ item.note || "" }}
                </template>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <template v-if="!previewDialog.isResult">
              <v-btn text class="px-4" @click="previewDialog.visible = false">
                取消
              </v-btn>
              <v-btn
                color="primary"
                class="btn-primary bg-gradient-primary px-4"
                :loading="bulkGrantLoading"
                @click="grantAnnualLeave(false)"
              >
                新增
              </v-btn>
            </template>
            <v-btn
              v-else
              color="primary"
              text
              class="btn-primary bg-gradient-primary px-4"
              @click="closePreviewDialog"
            >
              確定
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import InfoDialog from "@/components/Common/InfoDialog.vue";

import FormattedDate from "@/components/FormattedDate.vue";

export default {
  name: "AnnualLeaveBalancesWidget",
  components: {
    InfoDialog,
    FormattedDate,
  },
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    year: {
      type: [String, Number],
      default: () => new Date().getFullYear(),
    },
  },
  data() {
    return {
      loading: false,
      employees: [],
      currentPage: 1,
      itemsPerPage: 15,
      totalItems: 0,
      loadingMore: false,
      fetchError: false,
      expanded: [],
      selectedEmployees: [],
      bulkGrantLoading: false,
      infoDialog: {
        visible: false,
        title: "",
        message: "",
        highlightMessage: "",
        yesCallback: null,
      },
      previewDialog: {
        visible: false,
        message: "",
        isResult: false,
      },
      editDialog: {
        visible: false,
        loading: false,
        saving: false,
        item: null,
        record: null,
        usedDays: 0,
        remainingDays: 0,
        totalDays: 0,
      },
      previewRecords: [],
      totalPages: 1,
      displayUnit: localStorage.getItem("leave_duration_unit") || "hours",
    };
  },
  computed: {
    pageCount() {
      return this.totalPages || 1;
    },
    unitText() {
      return this.displayUnit === "hours" ? "小時" : "天";
    },
    headers() {
      return [
        { text: "員工", value: "name", align: "start" },
        {
          text: "任職日期",
          value: "expectedSignDate",
          align: "center",
        },
        {
          text: `特休剩餘 (${this.unitText})`,
          value: "annualAvailable",
          align: "center",
        },
        {
          text: `特休可用 (${this.unitText})`,
          value: "annualMax",
          align: "center",
        },
        { text: "操作", value: "actions", align: "center", sortable: false },
        //備註
        // { text: "備註", value: "note", align: "center", sortable: false },
      ];
    },
    selectedEmployeeIds() {
      return this.selectedEmployees.map((employee) => employee.id);
    },
    previewHeaders() {
      return [
        { text: "員工", value: "user_name", width: "8%" },
        { text: "類型", value: "join_type", width: "7%" },
        { text: "到職日期", value: "expected_sign_date", width: "9%" },
        { text: "年資", value: "seniority", width: "5%" },
        { text: `特休小時`, value: "total_minutes", width: "8%" },
        { text: "天數", value: "days", width: "5%" },
        // 生效日期
        { text: "生效日期", value: "effective_date", width: "9%" },
        { text: "有效期間", value: "duration", width: "18%" },

        //備註
        { text: "備註", value: "note", width: "15%" },
      ];
    },
  },
  async mounted() {
    await this.fetchLeaveBalances();
    window.addEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit,
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit,
    );
  },
  watch: {
    selectedCompany: {
      handler() {
        this.currentPage = 1;
        this.fetchLeaveBalances();
      },
      deep: true,
    },
  },
  methods: {
    convertMinutesToDays(minutes) {
      if (minutes == null) return "0";
      const val = parseFloat(minutes);
      if (isNaN(val)) return "0";
      if (this.displayUnit === "hours") {
        const hours = val / 60;
        return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
      }
      const days = val / 480;
      return Number.isInteger(days) ? days.toString() : days.toFixed(1);
    },
    formatLwopNote(lwop) {
      if (!lwop || !lwop.total_days) return "";
      const summary = `留職停薪 ${lwop.total_days} 天`;
      if (lwop.periods && lwop.periods.length > 0) {
        const formatDate = (iso) => {
          if (!iso) return "?";
          const d = new Date(iso);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const details = lwop.periods
          .map((p) => {
            const s = formatDate(p.start_time);
            const e = formatDate(p.end_time);
            return `${s} ~ ${e} (${p.days}天)`;
          })
          .join(", ");
        return `${summary} (${details})`;
      }
      return summary;
    },
    async fetchLeaveBalances() {
      this.loading = true;
      this.employees = [];
      this.fetchError = false;
      try {
        const currentCompanyID = this.selectedCompany
          ? this.selectedCompany.id
          : 1;
        const queryParams = new URLSearchParams({
          page: this.currentPage,
          limit: this.itemsPerPage,
          company_id: currentCompanyID,
        });
        const response = await this.$axios.get(
          `/api/leave-balances?${queryParams}`,
        );
        const rows =
          response.data && response.data.data ? response.data.data : [];
        const mappedEmployees = rows.map((item) => {
          const annualLeaveAvailable = parseFloat(item.annual_leave) || 0;
          const annualLeaveMax = parseFloat(item.annual_leave_max) || 0;

          const expectedSignDate =
            item.user && item.user.employeeProfile
              ? item.user.employeeProfile.expected_sign_date
              : null;

          return {
            id: item.user_id,
            name: item.user ? item.user.name : "Unknown",
            expectedSignDate,
            annualLeaveAvailable,
            annualLeaveMax,
            note: this.formatLwopNote(item.lwop),
          };
        });
        const pagination =
          response.data && response.data.pagination
            ? response.data.pagination
            : {};
        this.totalItems =
          Number(pagination.totalItems) || mappedEmployees.length || 0;
        this.totalPages = Number(pagination.totalPages) || 1;

        this.employees = mappedEmployees;
        this.expanded = [];
        this.selectedEmployees = [];
      } catch (error) {
        this.employees = [];
        this.totalItems = 0;
        this.fetchError = true;
        this.$emit("error", {
          title: "取得特休餘額失敗",
          message:
            error?.response?.data?.message ||
            error.message ||
            "無法取得特休餘額，請稍後再試。",
        });
      } finally {
        this.loading = false;
      }
    },
    forwardError(payload) {
      this.$emit("error", payload);
    },
    async openEditDialog(item) {
      this.editDialog.item = item;
      this.editDialog.visible = true;
      this.editDialog.loading = true;
      this.editDialog.record = null;
      try {
        const response = await this.$axios.get(
          `/api/annual-leaves/user/${item.id}`,
        );
        // Extract the currently active annualLeave from backend payload
        const annualLeave = response.data && response.data.annualLeave;
        if (annualLeave) {
          this.editDialog.record = annualLeave;
          const factor = this.displayUnit === "hours" ? 60 : 480;
          this.editDialog.totalDays =
            Math.round(((annualLeave.total_minutes || 0) / factor) * 100) / 100;
          this.editDialog.usedDays =
            Math.round(((annualLeave.used_minutes || 0) / factor) * 100) / 100;
          this.editDialog.remainingDays =
            Math.round(((annualLeave.remaining_minutes || 0) / factor) * 100) /
            100;
        }
      } catch (error) {
        this.$emit("error", {
          title: "無法取得有效紀錄",
          message:
            error?.response?.data?.message || "無法取得有效紀錄，請稍後再試。",
        });
      } finally {
        this.editDialog.loading = false;
      }
    },
    async saveEdit() {
      this.editDialog.saving = true;
      try {
        const factor = this.displayUnit === "hours" ? 60 : 480;
        const usedMinutes = Math.round(
          Number(this.editDialog.usedDays) * factor,
        );
        const totalMinutes = Math.round(
          Number(this.editDialog.totalDays) * factor,
        );
        const remainingMinutes = totalMinutes - usedMinutes;

        await this.$axios.put(
          `/api/annual-leaves/${this.editDialog.record.id}`,
          {
            used_minutes: usedMinutes,
            remaining_minutes: remainingMinutes,
          },
        );
        this.editDialog.visible = false;
        this.infoDialog.title = "更新成功";
        this.infoDialog.message = "特休天數已成功更新。";
        this.infoDialog.highlightMessage = "";
        this.infoDialog.yesCallback = async () => {
          this.infoDialog.yesCallback = null;
          await this.fetchLeaveBalances();
        };
        this.infoDialog.visible = true;
      } catch (error) {
        this.$emit("error", {
          title: "更新失敗",
          message:
            error?.response?.data?.message || "無法更新特休天數，請稍後再試。",
        });
      } finally {
        this.editDialog.saving = false;
      }
    },
    async grantAnnualLeave(preview = false) {
      if (!this.selectedEmployeeIds.length) return;
      if (!this.selectedCompany || !this.selectedCompany.id) {
        this.$emit("error", {
          title: "新增特休失敗",
          message: "請先選擇公司後再執行新增特休。",
        });
        return;
      }
      this.bulkGrantLoading = true;
      try {
        const uniqueUserIds = Array.from(new Set(this.selectedEmployeeIds));
        const response = await this.$axios.post("/api/annual-leaves/grant", {
          company_id: this.selectedCompany.id,
          user_ids: uniqueUserIds,
          preview,
          active_only: uniqueUserIds.length > 1,
        });
        const payload = response?.data?.data || {};

        if (preview) {
          this.showGrantPreviewDialog(payload);
        } else {
          // 實際新增成功
          this.previewDialog.visible = false;
          this.showGrantSuccessDialog(payload);
        }
      } catch (error) {
        this.$emit("error", {
          title: "新增特休失敗",
          message:
            error?.response?.data?.message ||
            error.message ||
            "新增特休時發生錯誤，請稍後再試。",
        });
      } finally {
        this.bulkGrantLoading = false;
      }
    },
    async closePreviewDialog() {
      this.previewDialog.visible = false;
      this.previewRecords = [];
      this.selectedEmployees = [];
      await this.fetchLeaveBalances();
    },
    rowClass(item) {
      return item.is_active ? "active-row" : "";
    },
    syncDisplayUnit() {
      this.displayUnit = localStorage.getItem("leave_duration_unit") || "hours";
    },
    toggleDisplayUnit() {
      this.displayUnit = this.displayUnit === "hours" ? "days" : "hours";
      localStorage.setItem("leave_duration_unit", this.displayUnit);
      window.dispatchEvent(new Event("leave-duration-unit-changed"));
    },
    formatPreviewDays(item) {
      const days = parseFloat(item.days) || 0;
      return Number.isInteger(days) ? days.toString() : days.toFixed(1);
    },
    formatTotalDays(item) {
      // Part-time: show leave_hours from formula_detail
      if (item.join_type === 2 && item.formula_detail) {
        const hours = parseFloat(item.formula_detail.leave_hours) || 0;
        return Number.isInteger(hours) ? hours.toString() : hours.toFixed(2);
      }
      // Full-time: convert days to hours
      const days = parseFloat(item.days) || 0;
      const hours = days * 8;
      return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
    },
    formatPartTimeNote(item) {
      if (!item.formula_detail) return "";
      if (item.actual_work_hours === 0 || item.actual_work_hours == null) {
        return "無工時資料，請先執行薪資結算";
      }
      const f = item.formula_detail;
      return `${f.actual_work_hours}區間內正常工作總時數 ÷ ${f.denominator}小時 × ${f.statutory_hours}小時 = ${f.leave_hours}小時`;
    },
    showGrantSuccessDialog(payload) {
      const createdRecords = payload.created_records || [];
      let message = "";

      if (createdRecords.length > 0) {
        message = createdRecords
          .map((r) => {
            const start = r.start_time ? r.start_time.substring(0, 10) : "-";
            const end = r.end_time ? r.end_time.substring(0, 10) : "-";
            const minutes = r.total_minutes || 0;
            const typeLabel = r.join_type === 2 ? "(部份工時)" : "";
            let amountStr;
            if (r.join_type === 2 && r.formula_detail) {
              const hours = parseFloat(r.formula_detail.leave_hours) || 0;
              amountStr = `${hours.toFixed(2)} 小時`;
            } else {
              const hoursPerDay = 8;
              const daysVal = minutes / (hoursPerDay * 60);
              const amount = Number.isInteger(daysVal)
                ? daysVal.toString()
                : daysVal.toFixed(1);
              amountStr = `${amount} 天`;
            }
            return `員工: ${r.user_name} ${typeLabel}\n有效期間: ${start} ~ ${end}\n額度: ${amountStr}`;
          })
          .join("\n\n");
      } else {
        const skippedCount = payload.skipped_count || 0;
        if (skippedCount > 0) {
          message = `成功新增 ${
            payload.created_count || 0
          } 筆特休，已略過 ${skippedCount} 筆。\n`;
          const skippedRecords = payload.skipped_records || [];
          if (skippedRecords.length > 0) {
            message +=
              "\n略過原因:\n" +
              skippedRecords
                .map((r) => `員工: ${r.user_name}\n${r.reason}`)
                .join("\n");
          }
        } else {
          message = `成功新增 ${payload.created_count || 0} 筆特休。`;
        }
      }

      this.infoDialog.title = "新增特休成功";
      this.infoDialog.message = message;
      this.infoDialog.highlightMessage = "請至特休假審核 進行審核";
      this.infoDialog.yesCallback = async () => {
        this.infoDialog.yesCallback = null;
        this.selectedEmployees = [];
        await this.fetchLeaveBalances();
      };
      this.infoDialog.visible = true;
    },
    showGrantPreviewDialog(payload) {
      this.previewDialog.isResult = false;
      this.previewRecords = (payload.preview_records || []).map((r) => ({
        ...r,
        note: this.formatLwopNote(r.lwop),
      }));
      const previewCount = payload.preview_count ?? this.previewRecords.length;
      const skippedCount = payload.skipped_count || 0;
      let message = "";

      if (skippedCount > 0 && previewCount == 0) {
        message = `預覽計算完成：預計產生 ${previewCount} 筆，已略過 ${skippedCount} 筆。\n`;
        const skippedRecords = payload.skipped_records || [];
        if (skippedRecords.length > 0) {
          message +=
            "\n略過原因:\n" +
            skippedRecords
              .map((r) => `員工: ${r.user_name}\n${r.reason}`)
              .join("\n");
        }
        this.infoDialog.title = "預覽成功";
        this.infoDialog.message = message;
        this.infoDialog.highlightMessage = "";
        this.infoDialog.visible = true;
      } else {
        message = `預覽計算完成：預計產生 ${previewCount} 筆。`;
        this.previewDialog.message = message;
        this.previewDialog.visible = true;
      }
    },
  },
};
</script>

<style scoped>
.annual-leave-table ::v-deep .v-data-table__expand-col {
  display: none;
}
::v-deep .active-row {
  background-color: #ef9a9a !important; /* Red 200 */
}
::v-deep .active-row:hover {
  background-color: #e57373 !important; /* Red 300 */
}
</style>
